import { consola } from 'consola'
import { computed, ref, watch, onMounted, onUnmounted, readonly } from 'vue'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useInstitutionStore } from './useInstitutionStore'

// Инициализируем плагины dayjs для работы с временными данными
// Следуем лучшим практикам Vue.js для работы с датами и временем
dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(duration)
dayjs.extend(isBetween)
dayjs.extend(customParseFormat)

/**
 * Composable для работы с рабочим временем филиалов
 */
export function useWorkingHours() {
  const institutionStore = useInstitutionStore()
  
  // Реактивное текущее время для автоматического обновления статуса
  const currentTime = ref(dayjs())
  let timeInterval: NodeJS.Timeout | null = null
  
  // Поддерживаемые форматы времени
  const TIME_FORMATS = ['HH:mm', 'H:mm', 'HH.mm', 'H.mm'] as const
  
  // Поддерживаемые варианты круглосуточной работы
  const FULL_DAY_INDICATORS = ['24/7', 'круглосуточно', '24h', 'always', 'круглые сутки'] as const

  /**
   * Парсит строку рабочего времени в объект с часами и минутами
   * @param timeString - строка времени в различных форматах
   * @returns объект с часами и минутами или null если формат неверный
   */
  function parseTime(timeString: string): { hours: number; minutes: number } | null {
    if (!timeString || typeof timeString !== 'string') return null
    
    const cleanTime = timeString.trim()
    
    // Пробуем парсить разные форматы времени с помощью day.js
    for (const format of TIME_FORMATS) {
      const parsed = dayjs(cleanTime, format, true)
      if (parsed.isValid()) {
        return {
          hours: parsed.hour(),
          minutes: parsed.minute(),
        }
      }
    }
    
    // Fallback на старый метод для совместимости
    const timeParts = cleanTime.split(/[.:]/);
    if (timeParts.length !== 2) return null
    
    const hours = Number.parseInt(timeParts[0], 10)
    const minutes = Number.parseInt(timeParts[1], 10)
    
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null
    
    return { hours, minutes }
  }

  /**
   * Парсит рабочие часы филиала с поддержкой различных форматов
   * @param workingHours - объект с рабочим временем
   * @param timezone - часовой пояс филиала (опционально)
   * @returns объект с временем открытия и закрытия или null
   */
  function parseWorkingHours(
    workingHours: { hours: string; timezone?: string } | null | undefined,
    timezone?: string,
  ): {
    open: { hours: number; minutes: number }
    close: { hours: number; minutes: number }
    isFullDay: boolean
    timezone: string | null
    crossesMidnight: boolean
  } | null {
    if (!workingHours?.hours) return null
    
    const hoursString = workingHours.hours.trim().toLowerCase()
    const branchTimezone = timezone || workingHours.timezone || null
    
    // Проверяем на круглосуточную работу
    if (FULL_DAY_INDICATORS.some(indicator => hoursString === indicator.toLowerCase())) {
      return {
        open: { hours: 0, minutes: 0 },
        close: { hours: 23, minutes: 59 },
        isFullDay: true,
        timezone: branchTimezone,
        crossesMidnight: false,
      }
    }
    
    // Парсим диапазон времени с поддержкой разных разделителей
    const timeRange = hoursString.split(/[–—-]/); // Поддержка разных тире
    if (timeRange.length !== 2) return null
    
    const openTime = parseTime(timeRange[0])
    const closeTime = parseTime(timeRange[1])
    
    if (!openTime || !closeTime) return null
    
    // Определяем, переходит ли рабочее время через полночь
    const openTotalMinutes = openTime.hours * 60 + openTime.minutes
    const closeTotalMinutes = closeTime.hours * 60 + closeTime.minutes
    const crossesMidnight = closeTotalMinutes <= openTotalMinutes
    
    return {
      open: openTime,
      close: closeTime,
      isFullDay: false,
      timezone: branchTimezone,
      crossesMidnight,
    }
  }

  /**
   * Проверяет, работает ли филиал в данный момент с поддержкой часовых поясов
   * @param workingHours - объект с рабочим временем
   * @param checkTime - время для проверки (по умолчанию текущее время)
   * @param timezone - часовой пояс для проверки
   * @returns true если филиал работает, false если закрыт
   */
  function isRestaurantOpen(
    workingHours: { hours: string; timezone?: string } | null | undefined,
    checkTime?: dayjs.Dayjs,
    timezone?: string,
  ): boolean {
    const parsedHours = parseWorkingHours(workingHours, timezone)
    if (!parsedHours) return false // Если не удается парсить - считаем закрытым
    
    if (parsedHours.isFullDay) return true // Круглосуточная работа
    
    // Используем указанное время или текущее время в нужном часовом поясе
    const timeToCheck = checkTime || currentTime.value
    const targetTimezone = parsedHours.timezone || timezone || dayjs.tz.guess()
    const localTime = timeToCheck.tz(targetTimezone)
    
    // Создаем объекты времени для сравнения
    const openTime = localTime
      .hour(parsedHours.open.hours)
      .minute(parsedHours.open.minutes)
      .second(0)
      .millisecond(0)
    
    let closeTime = localTime
      .hour(parsedHours.close.hours)
      .minute(parsedHours.close.minutes)
      .second(0)
      .millisecond(0)
    
    // Если рабочее время переходит через полночь
    if (parsedHours.crossesMidnight) {
      closeTime = closeTime.add(1, 'day')
      
      // Проверяем, находится ли время в диапазоне (может быть два периода)
      const isInFirstPeriod = localTime.isAfter(openTime) || localTime.isSame(openTime)
      const isInSecondPeriod = localTime.isBefore(closeTime.subtract(1, 'day'))
      
      return isInFirstPeriod || isInSecondPeriod
    }
    
    // Обычный случай - работа в пределах одного дня
    return localTime.isBetween(openTime, closeTime, 'minute', '[)')
  }

  /**
   * Получает время следующего открытия с учетом часовых поясов
   * @param workingHours - объект с рабочим временем
   * @param checkTime - время для проверки (по умолчанию текущее время)
   * @param timezone - часовой пояс
   * @returns объект с информацией о следующем открытии
   */
  function getNextOpeningTime(
    workingHours: { hours: string; timezone?: string } | null | undefined,
    checkTime?: dayjs.Dayjs,
    timezone?: string,
  ): {
    time: string;
    date: string;
    isToday: boolean;
    isTomorrow: boolean;
    formatted: string;
    remainingTime: string;
  } | null {
    const parsedHours = parseWorkingHours(workingHours, timezone)
    if (!parsedHours) return null
    
    if (parsedHours.isFullDay) return null // Круглосуточная работа
    
    // Используем указанное время или текущее время в нужном часовом поясе
    const timeToCheck = checkTime || currentTime.value
    const targetTimezone = parsedHours.timezone || timezone || dayjs.tz.guess()
    const localTime = timeToCheck.tz(targetTimezone)
    
    // Создаем время открытия на сегодня
    const todayOpenTime = localTime
      .hour(parsedHours.open.hours)
      .minute(parsedHours.open.minutes)
      .second(0)
      .millisecond(0)
    
    let nextOpenTime: dayjs.Dayjs
    let isToday = false
    let isTomorrow = false
    
    // Если время открытия сегодня еще не прошло
    if (localTime.isBefore(todayOpenTime)) {
      nextOpenTime = todayOpenTime
      isToday = true
    } else {
      // Открытие завтра
      nextOpenTime = todayOpenTime.add(1, 'day')
      isTomorrow = true
    }
    
    const timeString = nextOpenTime.format('HH:mm')
    const dateString = nextOpenTime.format('DD.MM.YYYY')
    const formattedString = isToday ? `сегодня в ${timeString}` : `завтра в ${timeString}`
    
    // Вычисляем оставшееся время (следующее открытие — сегодня или завтра, без «через N дней»)
    const duration = dayjs.duration(nextOpenTime.diff(localTime))
    const hours = Math.floor(duration.asHours())
    const minutes = duration.minutes()
    
    const remainingTime = hours > 0 ? `${hours}ч ${minutes}мин` : `${minutes}мин`;
    
    return {
      time: timeString,
      date: dateString,
      isToday,
      isTomorrow,
      formatted: formattedString,
      remainingTime,
    }
  }

  // Автоматическое обновление времени каждую минуту
  onMounted(() => {
    timeInterval = setInterval(() => {
      currentTime.value = dayjs()
    }, 60_000) // Обновляем каждую минуту
  })

  onUnmounted(() => {
    if (timeInterval) {
      clearInterval(timeInterval)
    }
  })

  /**
   * Проверяет, работает ли текущий выбранный филиал
   */
  const isCurrentBranchOpen = computed(() => {
    const selectedBranch = institutionStore.selectedBranch
    if (!selectedBranch) return true // Если филиал не выбран, не блокируем заказ
    
    return isRestaurantOpen(selectedBranch.working_hours, currentTime.value)
  })

  /**
   * Получает время следующего открытия текущего филиала
   */
  const nextOpeningTime = computed(() => {
    const selectedBranch = institutionStore.selectedBranch
    if (!selectedBranch) return null
    
    return getNextOpeningTime(selectedBranch.working_hours, currentTime.value)
  })

  /**
   * Получает расширенный статус работы филиала
   */
  const workingStatus = computed(() => {
    const selectedBranch = institutionStore.selectedBranch
    if (!selectedBranch) {
      return {
        isOpen: true,
        message: 'Филиал не выбран',
        nextAction: null,
        details: null,
      }
    }

    const isOpen = isCurrentBranchOpen.value
    const nextTime = nextOpeningTime.value
    const currentTimeFormatted = currentTime.value.format('HH:mm')
    
    if (isOpen) {
      // Вычисляем время до закрытия
      const parsedHours = parseWorkingHours(selectedBranch.working_hours)
      let closingSoon = false
      let timeToClose = ''
      
      if (parsedHours && !parsedHours.isFullDay) {
        const localTime = currentTime.value
        let closeTime = localTime
          .hour(parsedHours.close.hours)
          .minute(parsedHours.close.minutes)
          .second(0)
          .millisecond(0)
        
        if (parsedHours.crossesMidnight && localTime.isAfter(closeTime)) {
          closeTime = closeTime.add(1, 'day')
        }
        
        const minutesToClose = closeTime.diff(localTime, 'minutes')
        if (minutesToClose <= 60 && minutesToClose > 0) {
          closingSoon = true
          const hours = Math.floor(minutesToClose / 60)
          const mins = minutesToClose % 60
          timeToClose = hours > 0 ? `${hours}ч ${mins}мин` : `${mins}мин`
        }
      }
      
      return {
        isOpen: true,
        message: closingSoon ? 'Скоро закроется' : 'Открыто',
        nextAction: closingSoon ? `Закроется через ${timeToClose}` : null,
        details: {
          currentTime: currentTimeFormatted,
          closingSoon,
          timeToClose,
        },
      }
    }
    
    return {
      isOpen: false,
      message: 'Закрыто',
      nextAction: nextTime ? `Откроемся ${nextTime.formatted}` : 'Время работы уточняйте у администрации',
      details: {
        currentTime: currentTimeFormatted,
        nextOpening: nextTime,
      },
    }
  })

  // Форматированное текущее время для отображения
  const formattedCurrentTime = computed(() => {
    return currentTime.value.format('HH:mm:ss')
  })

  // Watcher для логирования изменений статуса
  watch(isCurrentBranchOpen, (newStatus, oldStatus) => {
    if (newStatus !== oldStatus) {
      consola.debug(`🏪 Branch status changed: ${oldStatus ? 'Open' : 'Closed'} → ${newStatus ? 'Open' : 'Closed'} at ${dayjs().format('HH:mm:ss')}`)
    }
  })

  /**
   * Получает полную диагностическую информацию о рабочем времени
   */
  function getWorkingHoursDiagnostics() {
    const selectedBranch = institutionStore.selectedBranch
    if (!selectedBranch) return null

    const parsedHours = parseWorkingHours(selectedBranch.working_hours)
    const nextOpening = nextOpeningTime.value
    const status = workingStatus.value

    return {
      branchName: selectedBranch.name || 'Unnamed branch',
      rawWorkingHours: selectedBranch.working_hours?.hours,
      parsedHours,
      currentTime: formattedCurrentTime.value,
      isOpen: isCurrentBranchOpen.value,
      status,
      nextOpening,
      timezone: parsedHours?.timezone || dayjs.tz.guess(),
      diagnostics: {
        crossesMidnight: parsedHours?.crossesMidnight || false,
        isFullDay: parsedHours?.isFullDay || false,
        lastStatusCheck: dayjs().format('DD.MM.YYYY HH:mm:ss'),
      },
    }
  }

  return {
    // Основные функции для работы с временем
    parseTime,
    parseWorkingHours,
    isRestaurantOpen,
    getNextOpeningTime,
    getWorkingHoursDiagnostics,
    
    // Computed properties для текущего филиала
    isCurrentBranchOpen,
    nextOpeningTime,
    workingStatus,
    formattedCurrentTime,
    
    // Реактивное текущее время
    currentTime: readonly(currentTime),
    
    // Дополнительные утилиты
    TIME_FORMATS,
    FULL_DAY_INDICATORS,
  }
}
